import React, { memo, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, ControlLabel, FormControl, FormGroup } from 'react-bootstrap';

import PropTypes from 'prop-types';
import { useFilters } from '../../../providers/Filters/useFilters';
import { validFiltersProfit } from '../../../utils/filters';

import { Dropdown, useDropdown } from '../../Dropdown';

const FilterProfitBody = ({ filterMethod }) => {
  const inputRef = useRef(null);
  const [filterSelected, setSelectedFilter] = useState({
    profit_from: null,
    profit_to: null,
    profit_negative: null, // 1,0,null
  });
  const { profit_from, profit_to, profit_negative } = filterSelected;
  const isCheckedProfit = !!profit_negative;

  const { close } = useDropdown();

  const { filters, onChangeFilter } = useFilters();
  const filter = filters[filterMethod];

  const onChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    value = value.replace(/,/g, '.');

    if (value.startsWith('.')) {
      value = `0${ value}`;
    }

    value = value.replace(/[^0-9\-.]/g, '');

    const hasMinus = value.startsWith('-');
    value = value.replace(/-/g, '');
    if (hasMinus) {
      value = `-${ value}`;
    }

    const parts = value.split('.');
    if (parts.length > 2) {
      value = `${parts[0] }.${ parts.slice(1).join('')}`;
    } else if (parts.length === 2) {
      parts[1] = parts[1].slice(0, 8);
      value = parts.join('.');
    }

    if (hasMinus && parts[0] === '' && parts[1] !== undefined) {
      value = `-0.${ parts[1]}`;
    }

    setSelectedFilter((prev) => ({
      ...prev,
      [name]: value === '' ? null : value,
    }));
  }

  const onBlur = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (value === '-') {
      value = '';
    } else if (value === '-0') {
      value = '0';
    }

    if (value) {
      const parts = value.split('.');
      let integerPart = parts[0];

      if (integerPart.startsWith('-')) {
        const digits = integerPart.slice(1).replace(/^0+/, '');
        integerPart = digits === '' ? '-0' : `-${digits}`;
      } else {
        integerPart = integerPart.replace(/^0+/, '') || '0';
      }

      parts[0] = integerPart;

      if (parts.length > 1 && parts[1] === '') {
        parts.pop();
      }

      value = parts.join('.');

      if (value === '-0') {
        value = '0';
      }
    }

    if (value === '') {
      value = null;
    }

    setSelectedFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onChangeNegativeProfit = () => {
    setSelectedFilter((prevState) => ({
      ...prevState,
      profit_negative: prevState.profit_negative ? null : '1',
    }))
  }

  const onSubmitFilter = (e) => {
    e.preventDefault();
    try {
      if (!filter.profit_from && !filter.profit_to && !filter.profit_negative && !filterSelected.profit_from && !filterSelected.profit_to && !filterSelected.profit_negative) {
        return;
      }

      onChangeFilter({
        [filterMethod]: filterSelected,
      })
    } finally {
      close();
    }
  }

  useEffect(() => {
    const isValidProfitFrom = validFiltersProfit(filter.profit_from);
    const isValidProfitTo = validFiltersProfit(filter.profit_to);
    const isValidProfitNegative = filter.profit_negative === 1 || filter.profit_negative === '1';

    setSelectedFilter({
      profit_from: isValidProfitFrom ? filter.profit_from : '',
      profit_to: isValidProfitTo ? filter.profit_to : '',
      profit_negative: isValidProfitNegative ? filter.profit_negative : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form onSubmit={onSubmitFilter}>
      <Dropdown.Body pl={16} pr={16}>
        <FormGroup>
          <ControlLabel htmlFor="profit_from">From</ControlLabel>
          <FormControl
            inputRef={(ref) => { inputRef.current = ref }}
            autoComplete="off"
            placeholder="Min"
            value={profit_from || ''}
            onBlur={onBlur}
            onChange={onChange}
            id="profit_from"
            name="profit_from"
            type="text"
            inputMode="numeric"
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel htmlFor="profit_to">To</ControlLabel>
          <FormControl
            autoComplete="off"
            placeholder="Max"
            value={profit_to || ''}
            onBlur={onBlur}
            onChange={onChange}
            id="profit_to"
            name="profit_to"
            type="text"
            inputMode="numeric"
          />
        </FormGroup>
        <FormGroup style={{ marginBottom: 0 }}>
          <Checkbox
            id="profit_negative"
            name="profit_negative"
            onChange={onChangeNegativeProfit}
            checked={isCheckedProfit}
            style={{ margin: 0 }}
          >
            Negative profit only
          </Checkbox>
        </FormGroup>
      </Dropdown.Body>
      <Dropdown.Footer p={16}>
        <Button
          block
          type="submit"
          bsStyle="primary"
        >
          Apply
        </Button>
      </Dropdown.Footer>
    </form>
  );
}

FilterProfitBody.propTypes = {
  filterMethod: PropTypes.string,
};

export default memo(FilterProfitBody);
